import { createFileRoute } from '@tanstack/react-router'
import * as React from 'react'

import { PageHeader } from '@/components/PageHeader'
import { UsageChartCard } from '@/components/UsageChart'
import { usageKeys } from '@/components/usage/keys'
import { UsageByCredentialCard } from '@/components/usage/usage-by-credential'
import { UsageStatStrip } from '@/components/usage/usage-stat-strip'
import { orgQueries } from '@/lib/org/queries'
import { getOrgUsage } from '@/lib/usage/server'
import type { UsageRange } from '@/lib/usage/usage'

export const Route = createFileRoute('/_authenticated/usage')({
  // Every card on this page reads the 30d window the page opens on, so prefetch
  // it. Nothing is awaited: each card already renders its own skeleton, and the
  // range toggle can move off 30d anyway.
  loader: async ({ context: { queryClient, activeOrg } }) => {
    if (!activeOrg) return
    // Awaited under SSR only — see dashboard.tsx: an un-awaited prefetch that
    // resolves after render but before dehydration is a hydration mismatch.
    const p = queryClient.prefetchQuery(orgQueries.usage(activeOrg.id, '30d'))
    if (import.meta.env.SSR) await p
  },
  component: Usage,
})

function Usage() {
  const { activeOrg } = Route.useRouteContext()
  const orgId = activeOrg?.id ?? null
  // One range drives the whole page — the strip, the chart, and the
  // per-credential ranking all read this window. The chart's toggle is the
  // single control that changes it.
  const [range, setRange] = React.useState<UsageRange>('30d')

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Usage"
        description="Bandwidth your organization has consumed over time."
      />

      <UsageStatStrip orgId={orgId} range={range} />

      <UsageChartCard
        title="Data usage"
        description="Bandwidth consumed across all of this organization's credentials."
        queryKey={(r) => usageKeys.orgUsage(orgId, r)}
        fetcher={(r) => getOrgUsage({ data: { range: r } })}
        range={range}
        onRangeChange={setRange}
      />

      <UsageByCredentialCard orgId={orgId} range={range} />
    </div>
  )
}
